import { Component } from "@theme/component";
import { VariantSelectedEvent, VariantUpdateEvent } from "@theme/events";
import { morph } from "@theme/morph-dom";
import { requestIdleCallback } from "@theme/utilities";

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [Refs = {}]
 *
 * @extends Component<Refs>
 */
export default class VariantPickerComponent extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  /**
   * The base product URL without query parameters.
   * Used as the foundation for building variant request URLs.
   * @type {string | null}
   */
  #productUrl = null;

  /**
   * Reference to the loading spinner element (`.theme__loading`).
   * This is toggled during variant fetch requests.
   * @type {HTMLElement | null}
   */
  #themeLoadingNode = null;

  /**
   * Lifecycle hook that is called when the element is inserted into the DOM.
   * Initializes event listeners, product URL state, and sets up variant prefetching.
   */
  connectedCallback() {
    super.connectedCallback();

    this.addEventListener("change", this.variantChanged.bind(this));

    let { productUrl } = this.dataset;
    this.#productUrl = productUrl?.includes("?")
      ? productUrl.split("?")[0]
      : productUrl;

    // If inside a quick add modal, scope loading indicator to that modal
    const themeLoadingParentNode = this.closest(".quick-add-modal") || document;
    this.#themeLoadingNode =
      themeLoadingParentNode.querySelector(".theme__loading");

    // Shopify disables caching on PDP fetches, so we keep responses in an in-memory cache.
    // 🎗️ TODO: Invalidate the cache object and refetch after a certain amount of time to ensure up-to-date data for PDP page long sessions
    window.Theme.prefetchCache = window.Theme.prefetchCache || {};
    window.Theme.prefetchCache[this.#productUrl] =
      window.Theme.prefetchCache[this.#productUrl] || {};

    if ("scheduler" in window) {
      window.scheduler.postTask(() => this.#prefetchVariantRequests(), {
        priority: "background",
      });
    } else {
      requestIdleCallback(() => this.#prefetchVariantRequests());
    }
  }

  /**
   * Prefetches a single variant request with exponential backoff for retrying on rate limits.
   * Stores successful responses in the in-memory prefetch cache.
   *
   * @private
   * @param {string} optionValuesCsv - A CSV string of option values to fetch.
   * @throws {Error} If the request fails and retries are exhausted.
   * @returns {Promise<void>} Resolves when the request is completed or cached.
   */
  async #prefetchVariantRequest(optionValuesCsv) {
    const requestUrl = `${this.#productUrl}?option_values=${optionValuesCsv}`;

    if (!window.Theme.prefetchCache[this.#productUrl][requestUrl]) {
      const retries = 3;
      const backoff = 1000; // 1 second backoff constant

      for (let attempt = 0; attempt <= retries; attempt++) {
        const response = await fetch(requestUrl, { priority: "low" });

        if (response.status >= 200 && response.status < 300) {
          window.Theme.prefetchCache[this.#productUrl][requestUrl] =
            await response.text();
          return;
        }

        if (response.status === 429) {
          // Retry with exponential backoff on rate limiting
          const delay = backoff * Math.pow(2, attempt);

          if (attempt < retries) {
            await new Promise((resolve) => setTimeout(resolve, delay));
            continue; // retry
          }
        }

        // ❌ Non-2xx and not retry-able (or retries exhausted)
        throw new Error(`Request failed with status ${response.status}`);
      }
    }
  }

  /**
   * Prefetches all variant requests for the current product if on a product page
   * and no variant is currently selected in the URL. Uses background scheduling
   * (`scheduler.postTask` or `requestIdleCallback`) to avoid blocking user interactions.
   *
   * Prefetches all variant requests using a concurrent queue to maintain a steady, low-priority
   * stream of requests to avoid rate-limiting and to prevent blocking of the main thread.
   *
   * @private
   * @returns {void}
   */
  async #prefetchVariantRequests() {
    const optionValuesScriptNode = document.getElementById(
      "variant_prefetch_option_values",
    );

    if (optionValuesScriptNode) {
      const optionValuesCsvs = JSON.parse(optionValuesScriptNode.textContent);
      const concurrencyLimit = 3; // Process up to 3 requests at a time
      const queue = [...optionValuesCsvs]; // Clone the array to create a mutable queue

      const runTask = async () => {
        while (queue.length > 0) {
          const optionValuesCsv = queue.shift(); // Get the next task

          const prefetchTask = () =>
            this.#prefetchVariantRequest(optionValuesCsv);

          // Schedule the prefetch task with low priority
          if ("scheduler" in window) {
            window.scheduler.postTask(prefetchTask, {
              priority: "background",
            });
          } else {
            requestIdleCallback(prefetchTask);
          }
        }
      };

      // Create a pool of "workers" (promises)
      const workerPool = [];
      for (let i = 0; i < concurrencyLimit; i++) {
        workerPool.push(runTask());
      }

      // Wait for all tasks in the queue to be processed
      await Promise.all(workerPool);
    }
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    this.updateSelectedOption(event.target);
    this.dispatchEvent(
      new VariantSelectedEvent({
        id: event.target.dataset.optionValueId ?? "",
      }),
    );

    const isOnProductPage =
      Theme.template.name === "product" &&
      !event.target.closest("product-card-component") &&
      !event.target.closest("quick-add-dialog");

    // Morph the entire main content for combined listings child products, because changing the product
    // might also change other sections depending on recommendations, metafields, etc.
    const currentUrl = this.dataset.productUrl?.split("?")[0];
    const newUrl = event.target.dataset.connectedProductUrl;
    const loadsNewProduct =
      isOnProductPage && !!newUrl && newUrl !== currentUrl;

    this.fetchUpdatedSection(
      this.buildRequestUrl(event.target),
      loadsNewProduct,
    );

    // Keep browser history in sync with the selected variant/product
    const url = new URL(window.location.href);

    if (isOnProductPage) {
      if (event.target.dataset.variantId) {
        url.searchParams.set("variant", event.target.dataset.variantId);
      } else {
        url.searchParams.delete("variant");
      }
    }

    // Change the path if the option is connected to another product via combined listing.
    if (loadsNewProduct) {
      url.pathname = newUrl;
    }

    if (url.href !== window.location.href) {
      history.replaceState({}, "", url.toString());
    }
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === "string") {
      const targetElement = this.querySelector(
        `[data-option-value-id="${target}"]`,
      );

      if (!targetElement) throw new Error("Target element not found");

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find(
        (option) => option.value === newValue,
      );

      if (!newSelectedOption) throw new Error("Option not found");

      for (const option of target.options) {
        option.removeAttribute("selected");
      }

      newSelectedOption.setAttribute("selected", "selected");
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @returns {string} The request URL.
   */
  buildRequestUrl(
    selectedOption,
    source = null,
    sourceSelectedOptionsValues = [],
  ) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    let productUrl =
      selectedOption.dataset.connectedProductUrl ||
      this.#pendingRequestUrl ||
      this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];

    if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(",")}`);
    } else if (source === "product-card-component") {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(",")}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    // If variant-picker-component is a child of quick-add-component or swatches-variant-picker-component, we need to append section_id=section-rendering-product-card to the URL
    if (
      this.closest("quick-add-component") ||
      this.closest("swatches-variant-picker-component")
    ) {
      if (productUrl?.includes("?")) {
        productUrl = productUrl.split("?")[0];
      }
      return `${productUrl}?section_id=section-rendering-product-card&${params.join("&")}`;
    }
    return `${productUrl}?${params.join("&")}`;
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {boolean} shouldMorphMain - If the entire main content should be morphed. By default, only the variant picker is morphed.
   */
  fetchUpdatedSection(requestUrl, shouldMorphMain = false) {
    requestAnimationFrame(() =>
      this.#themeLoadingNode?.classList.remove("hidden"),
    );

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    const cachedData = window.Theme.prefetchCache[this.#productUrl][requestUrl];
    const cachedFetch = () => new Promise((resolve) => resolve(cachedData));

    const fetchPromise = cachedData
      ? cachedFetch()
      : fetch(requestUrl, { signal: this.#abortController.signal }).then(
          (response) => response.text(),
        );

    fetchPromise
      .then((responseText) => {
        // Cache response if it wasn't already prefetched
        const prefetchCache = window.Theme.prefetchCache[this.#productUrl];
        if (!prefetchCache[requestUrl]) {
          prefetchCache[requestUrl] = responseText;
        }

        this.#pendingRequestUrl = undefined;
        const html = new DOMParser().parseFromString(responseText, "text/html");
        // Defer is only useful for the initial rendering of the page. Remove it here.
        html.querySelector("overflow-list[defer]")?.removeAttribute("defer");

        const textContent = html.querySelector(
          `variant-picker-component script[type="application/json"]`,
        )?.textContent;
        if (!textContent) return;

        if (shouldMorphMain) {
          this.updateMain(html);
        } else {
          const newProduct = this.updateVariantPicker(html);

          // We grab the variant object from the response and dispatch an event with it.
          if (this.selectedOptionId) {
            this.dispatchEvent(
              new VariantUpdateEvent(
                JSON.parse(textContent),
                this.selectedOptionId,
                {
                  html,
                  productId: this.dataset.productId ?? "",
                  newProduct,
                },
              ),
            );
          }
        }
      })
      .catch((error) => {
        if (error.name === "AbortError") {
          console.info("Fetch aborted by user");
        } else {
          console.error(error);
        }
      })
      .finally(() => {
        requestAnimationFrame(() =>
          this.#themeLoadingNode?.classList.add("hidden"),
        );
      });
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(
      this.tagName.toLowerCase(),
    );

    if (!newVariantPickerSource) {
      throw new Error("No new variant picker source found");
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (
        newProductId &&
        newProductUrl &&
        this.dataset.productId !== newProductId
      ) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    morph(this, newVariantPickerSource);

    return newProduct;
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector("main");
    const newMain = newHtml.querySelector("main");

    if (!main || !newMain) {
      throw new Error("No new main source found");
    }

    morph(main, newMain);
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector(
      "select option[selected], fieldset input:checked",
    );

    if (
      !(
        selectedOption instanceof HTMLInputElement ||
        selectedOption instanceof HTMLOptionElement
      )
    ) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error("No option value ID found");
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(
      this.querySelectorAll("select option[selected], fieldset input:checked"),
    );

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error("No option value ID found");

      return optionValueId;
    });
  }
}

if (!customElements.get("variant-picker-component")) {
  customElements.define("variant-picker-component", VariantPickerComponent);
}
